import React, { useState, useEffect, useMemo } from "react";
import { apiGet } from "../../services/api";
import "./CalendarView.css";

const toIsoDate = (date: Date | string): string => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split("T")[0];
};

export interface PatientSelectPayload {
  patient_id: string;
  phone?: string;
  name?: string;
  gender?: string;
  age?: number;
}

export interface CalendarViewProps {
  refreshKey?: number;
  onPatientSelect?: (p: PatientSelectPayload) => void;
}

interface VisitPatient {
  visit_id: number;
  patient_id: string;
  patient_name?: string;
  visit_type?: string;
  gender?: string;
  age?: number;
}

const CalendarView = ({ refreshKey, onPatientSelect }: CalendarViewProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(toIsoDate(new Date()));
  const [patients, setPatients] = useState<VisitPatient[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPatientsByDate = async (date: string) => {
    if (!date) return;
    setLoading(true);
    try {
      const data = await apiGet(`/api/visits-by-date?date=${date}`) as { visits?: VisitPatient[] };
      setPatients(Array.isArray(data?.visits) ? data.visits : []);
    } catch (error) {
      console.error("Calendar fetch failed", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientsByDate(selectedDate);
  }, [selectedDate, refreshKey]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  }, [currentMonth]);

  const handleNav = (direction: number) => {
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + direction,
        1
      )
    );
  };

  const jumpToToday = () => {
    const now = new Date();
    setCurrentMonth(now);
    setSelectedDate(toIsoDate(now));
  };

  const isToday = (date: Date): boolean => toIsoDate(date) === toIsoDate(new Date());
  const isSelected = (date: Date): boolean => toIsoDate(date) === selectedDate;

  return (
    <div className="calendar-widget">
      <div className="cal-header">
        <div className="cal-title">
          <span className="cal-month-name">
            {currentMonth.toLocaleDateString("en-US", { month: "long" })}
          </span>
          <span className="cal-year-name">{currentMonth.getFullYear()}</span>
        </div>
        <div className="cal-controls">
          <button
            className="btn-icon-nav"
            onClick={() => handleNav(-1)}
            title="Previous"
          >
            ◀
          </button>
          <button className="btn-today-text" onClick={jumpToToday}>
            Today
          </button>
          <button
            className="btn-icon-nav"
            onClick={() => handleNav(1)}
            title="Next"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="cal-body">
        <div className="cal-weekdays">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div key={day} className="cal-weekday">
              {day}
            </div>
          ))}
        </div>
        <div className="cal-grid">
          {calendarDays.map((date, i) => {
            if (!date) {
              return <div key={`empty-${i}`} className="cal-day empty" />;
            }
            return (
              <button
                key={i}
                className={["cal-day", isSelected(date) ? "selected" : "", isToday(date) ? "today" : ""].join(" ")}
                onClick={() => setSelectedDate(toIsoDate(date))}
              >
                <span className="day-number">{date.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="cal-agenda">
        <div className="agenda-header">
          <h5>
            {new Date(selectedDate).toLocaleDateString("en-US", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </h5>
          <span className="badge-count">{patients.length} Visits</span>
        </div>
        <div className="agenda-list">
          {loading ? (
            <div className="agenda-loading">Loading visits...</div>
          ) : patients.length > 0 ? (
            patients.map((p) => (
              <div
                key={p.visit_id}
                className="agenda-item"
                onClick={() =>
                  onPatientSelect?.({
                    patient_id: p.patient_id,
                    phone: p.patient_id,
                    name: p.patient_name,
                    gender: p.gender,
                    age: p.age,
                  })
                }
              >
                <div className="patient-slot">
                  <strong>{p.patient_name}</strong>
                  <small>#{p.patient_id}</small>
                </div>
                <span className={`type-tag ${p.visit_type === "New" ? "new" : "review"}`}>
                  {p.visit_type}
                </span>
              </div>
            ))
          ) : (
            <div className="agenda-empty">
              <span>📅</span> No appointments
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
